import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getSalesReport, getTopProducts, getBottomProducts, getBranches, getUsers, getCompanyConfig, getInvoicesPaged, getCreditNotes } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  FileText, TrendingUp, TrendingDown, DollarSign,
  Building2, Filter, Download, BarChart2
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import * as XLSX from 'xlsx';

const COLORS = ['#06B6D4', '#F43F5E', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899'];

const money = (n) =>
  `RD$${parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const paymentLabels = {
  efectivo: 'Efectivo', tarjeta: 'Tarjeta',
  transferencia: 'Transferencia', credito: 'Crédito',
};

// Returns "YYYY-MM-DD" for current date in given IANA timezone
const todayInTZ = (tz) => new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC' }).format(new Date());

// Quick period presets — dates in the company's configured timezone
const getPreset = (preset, tz = 'UTC') => {
  const todayStr = todayInTZ(tz);
  const [y, m, d] = todayStr.split('-').map(Number);

  switch (preset) {
    case 'today':
      return { start: todayStr, end: todayStr };
    case 'week': {
      const weekday = new Intl.DateTimeFormat('en', { timeZone: tz, weekday: 'short' }).format(new Date());
      const dow = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[weekday] ?? 0;
      const daysFromMon = dow === 0 ? 6 : dow - 1;
      const monDate = new Date(Date.UTC(y, m - 1, d - daysFromMon));
      const monStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC' }).format(monDate);
      return { start: monStr, end: todayStr };
    }
    case 'month':
      return { start: `${y}-${String(m).padStart(2, '0')}-01`, end: todayStr };
    case 'year':
      return { start: `${y}-01-01`, end: todayStr };
    default:
      return { start: '', end: '' };
  }
};

const Reports = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [branches, setBranches] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [branchId, setBranchId] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [salesReport, setSalesReport] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [bottomProducts, setBottomProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activePreset, setActivePreset] = useState('month');
  const [companyTz, setCompanyTz] = useState('America/Santo_Domingo');

  useEffect(() => {
    if (isAdmin) getBranches().then(b => setBranches(b || [])).catch(() => {});
    getCompanyConfig().then(c => {
      const tz = c?.timezone || 'America/Santo_Domingo';
      setCompanyTz(tz);
      const preset = getPreset('month', tz);
      setStartDate(preset.start);
      setEndDate(preset.end);
      loadReports(preset.start, preset.end, '', '');
    }).catch(() => {
      const tz = 'America/Santo_Domingo';
      const preset = getPreset('month', tz);
      setStartDate(preset.start);
      setEndDate(preset.end);
      loadReports(preset.start, preset.end, '', '');
    });
  }, [isAdmin]); // eslint-disable-line

  const loadReports = useCallback(async (sd = startDate, ed = endDate, bid = branchId, pm = paymentMethodFilter) => {
    setLoading(true);
    try {
      const filters = {};
      if (sd) filters.start_date = sd;
      if (ed) filters.end_date = ed;
      if (bid) filters.branch_id = bid;
      if (pm) filters.payment_method = pm;

      const [salesData, topData, bottomData] = await Promise.all([
        getSalesReport(filters),
        getTopProducts(10),
        getBottomProducts(10),
      ]);
      setSalesReport(salesData);
      setTopProducts(topData);
      setBottomProducts(bottomData);
    } catch {
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, branchId]);

  const handlePreset = (preset) => {
    const { start, end } = getPreset(preset, companyTz);
    setStartDate(start);
    setEndDate(end);
    setActivePreset(preset);
    loadReports(start, end, branchId, paymentMethodFilter);
  };

  const handleApply = () => {
    setActivePreset('custom');
    loadReports(startDate, endDate, branchId, paymentMethodFilter);
  };

  const handlePaymentFilter = (pm) => {
    const next = paymentMethodFilter === pm ? '' : pm;
    setPaymentMethodFilter(next);
    loadReports(startDate, endDate, branchId, next);
  };

  // ── Excel export ──
  const exportExcel = async () => {
    if (!salesReport) return;
    setExporting(true);
    try {
      const filters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate)   filters.end_date   = endDate;
      if (branchId)  filters.branch_id  = branchId;

      const [invoicesData, ncData] = await Promise.all([
        getInvoicesPaged({ ...filters, ...(paymentMethodFilter ? { payment_method: paymentMethodFilter } : {}) }, 1, 5000),
        getCreditNotes(filters),
      ]);
      const allInvoices = invoicesData.invoices || invoicesData || [];
      const allNc       = Array.isArray(ncData) ? ncData : [];

      const pmLabels = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', credito: 'Crédito', nota_credito: 'Nota de Crédito' };
      const wb = XLSX.utils.book_new();

      // ── Hoja 1: Resumen ──
      const resumenRows = [
        ['REPORTE DE VENTAS'],
        ['Período', `${startDate} al ${endDate}`],
        branchId ? ['Sucursal', branches.find(b => b.id === branchId)?.name || branchId] : [],
        [],
        ['RESUMEN GENERAL'],
        ['Ventas brutas (RD$)',  salesReport.total_sales],
        ['NC emitidas (RD$)',   -(salesReport.total_nc || 0)],
        ['Ventas netas (RD$)',   salesReport.net_sales ?? salesReport.total_sales],
        ['Facturas emitidas',    salesReport.total_invoices],
      ];

      if (salesReport.by_payment_method && Object.keys(salesReport.by_payment_method).length) {
        resumenRows.push([], ['MÉTODOS DE PAGO'], ['Método', 'Facturas', 'Total RD$']);
        Object.entries(salesReport.by_payment_method).forEach(([m, d]) => {
          resumenRows.push([pmLabels[m] || m, d.count, d.total]);
        });
      }

      if (salesReport.by_branch && Object.keys(salesReport.by_branch).length > 1) {
        resumenRows.push([], ['SUCURSALES'], ['Sucursal', 'Facturas', 'Total RD$']);
        Object.values(salesReport.by_branch).forEach(b => {
          resumenRows.push([b.branch_name, b.count, b.total]);
        });
      }

      const wsResumen = XLSX.utils.aoa_to_sheet(resumenRows.filter(r => r !== null));
      wsResumen['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

      // ── Hoja 2: Ventas por Día ──
      if (salesReport.daily_sales?.length) {
        const daily = [
          ['Fecha', 'Ventas RD$', 'Facturas'],
          ...salesReport.daily_sales.map(d => [d.day, d.total, d.count]),
        ];
        const wsDaily = XLSX.utils.aoa_to_sheet(daily);
        wsDaily['!cols'] = [{ wch: 14 }, { wch: 16 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, wsDaily, 'Ventas por día');
      }

      // ── Hoja 3: Detalle de Facturas ──
      if (allInvoices.length) {
        const sorted = [...allInvoices].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
        const invRows = [
          ['N° Factura', 'Fecha', 'Cliente', 'Método de pago', 'Sucursal', 'Vendedor', 'Total RD$'],
          ...sorted.map(i => [
            i.invoice_number,
            (i.created_at || '').slice(0, 10),
            i.customer_name || 'Consumidor Final',
            pmLabels[i.payment_method] || i.payment_method || '',
            i.branch_name || '',
            i.created_by_name || '',
            parseFloat(i.total || 0),
          ]),
        ];
        const wsInv = XLSX.utils.aoa_to_sheet(invRows);
        wsInv['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 24 }, { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, wsInv, 'Detalle facturas');
      }

      // ── Hoja 4: NC Emitidas ──
      if (allNc.length) {
        const ncRows = [
          ['N° NC', 'Fecha', 'Factura ref.', 'Cliente', 'Razón', 'Monto RD$', 'Estado'],
          ...allNc.map(cn => [
            cn.credit_note_number || '',
            (cn.created_at || '').slice(0, 10),
            cn.invoice_number || cn.invoice_id || '',
            cn.customer_name || '',
            cn.reason || '',
            parseFloat(cn.amount || 0),
            cn.status || 'pendiente',
          ]),
        ];
        const wsNc = XLSX.utils.aoa_to_sheet(ncRows);
        wsNc['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 30 }, { wch: 14 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, wsNc, 'NC emitidas');
      }

      // ── Hoja 5: Top Productos ──
      if (topProducts.length) {
        const topRows = [
          ['Producto', 'Cantidad', 'Ingresos RD$'],
          ...topProducts.map(p => [p.product_name, p.quantity, parseFloat(p.revenue || 0)]),
        ];
        const wsTop = XLSX.utils.aoa_to_sheet(topRows);
        wsTop['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, wsTop, 'Top productos');
      }

      XLSX.writeFile(wb, `reporte_ventas_${startDate}_${endDate}.xlsx`);
      toast.success('Reporte exportado');
    } catch {
      toast.error('Error al exportar reporte');
    } finally {
      setExporting(false);
    }
  };

  // Chart data
  const paymentChartData = salesReport?.by_payment_method
    ? Object.entries(salesReport.by_payment_method).map(([m, d]) => ({
        name: paymentLabels[m] || m, value: d.total, count: d.count,
      }))
    : [];

  const branchChartData = salesReport?.by_branch
    ? Object.entries(salesReport.by_branch).map(([, d]) => ({
        name: d.branch_name, ventas: d.total, facturas: d.count,
      }))
    : [];

  const dailyChartData = (salesReport?.daily_sales || []).map(d => ({
    day: d.day.slice(5), ventas: d.total,
  }));

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="reports-page">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Reportes</h1>
        <Button
          onClick={exportExcel}
          disabled={!salesReport || loading || exporting}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Filter className="w-5 h-5 text-cyan-500" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick presets */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'today', label: 'Hoy' },
              { key: 'week',  label: 'Esta semana' },
              { key: 'month', label: 'Este mes' },
              { key: 'year',  label: 'Este año' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handlePreset(key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activePreset === key
                    ? 'bg-cyan-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Payment method filter */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-slate-500 font-medium">Método de pago:</span>
            {[
              { key: '',              label: 'Todos' },
              { key: 'efectivo',      label: '💵 Efectivo' },
              { key: 'tarjeta',       label: '💳 Tarjeta' },
              { key: 'transferencia', label: '🏦 Transferencia' },
              { key: 'credito',       label: '📋 Crédito' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handlePaymentFilter(key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  paymentMethodFilter === key
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Date + branch filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label>Desde</Label>
              <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setActivePreset('custom'); }}
                data-testid="report-start-date" />
            </div>
            <div className="space-y-1">
              <Label>Hasta</Label>
              <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setActivePreset('custom'); }}
                data-testid="report-end-date" />
            </div>
            {isAdmin && (
              <div className="space-y-1">
                <Label>Sucursal</Label>
                <Select value={branchId || 'ALL'} onValueChange={v => setBranchId(v === 'ALL' ? '' : v)}>
                  <SelectTrigger data-testid="report-branch-select">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todas las sucursales</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-end">
              <Button onClick={handleApply} disabled={loading}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="generate-report-button">
                {loading ? 'Cargando...' : 'Aplicar filtros'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {salesReport && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white shadow-lg" data-testid="total-sales-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-cyan-100">Ventas Brutas</CardTitle>
              <DollarSign className="w-5 h-5 text-cyan-200" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                {money(salesReport.total_sales)}
              </div>
              <p className="text-xs text-cyan-100 mt-1">{salesReport.total_invoices} facturas</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-emerald-100">Ventas Netas</CardTitle>
              <TrendingUp className="w-5 h-5 text-emerald-200" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                {money(salesReport.net_sales ?? salesReport.total_sales)}
              </div>
              <p className="text-xs text-emerald-100 mt-1">Brutas − NC emitidas</p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">NC Emitidas</CardTitle>
              <TrendingDown className="w-5 h-5 text-red-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-500" style={{ fontFamily: 'JetBrains Mono' }}>
                {money(salesReport.total_nc || 0)}
              </div>
              <p className="text-xs text-slate-500 mt-1">En el período</p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Facturas emitidas</CardTitle>
              <FileText className="w-5 h-5 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                {salesReport.total_invoices}
              </div>
              <p className="text-xs text-slate-500 mt-1">En el período</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts row 1 */}
      {salesReport && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Daily sales line chart */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <BarChart2 className="w-5 h-5 text-cyan-500" />
                Ventas por Día
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dailyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={dailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8"
                      tickFormatter={v => `RD$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => [money(v), 'Ventas']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Line type="monotone" dataKey="ventas" stroke="#06B6D4" strokeWidth={2}
                      dot={{ r: 3, fill: '#06B6D4' }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                  No hay datos para el período
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment methods pie */}
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="payment-methods-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <DollarSign className="w-5 h-5 text-cyan-500" />
                Por Método de Pago
              </CardTitle>
            </CardHeader>
            <CardContent>
              {paymentChartData.length > 0 ? (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="60%" height={220}>
                    <PieChart>
                      <Pie data={paymentChartData} cx="50%" cy="50%"
                        innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="value">
                        {paymentChartData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={v => [money(v), 'Total']} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2">
                    {paymentChartData.map((p, i) => (
                      <div key={p.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-slate-600">{p.name}</span>
                        </div>
                        <span className="font-medium text-slate-700">{money(p.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                  No hay datos disponibles
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sucursales — admin only */}
      {salesReport && isAdmin && branchChartData.length > 1 && !branchId && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Building2 className="w-5 h-5 text-cyan-500" />
              Ventas por Sucursal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Sucursal</th>
                      <th className="text-right p-3 font-medium text-slate-600">Facturas</th>
                      <th className="text-right p-3 font-medium text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchChartData.map((b, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50">
                        <td className="p-3">{b.name}</td>
                        <td className="p-3 text-right">{b.facturas}</td>
                        <td className="p-3 text-right font-medium">{money(b.ventas)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={branchChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8"
                    tickFormatter={v => `RD$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [money(v), 'Ventas']} />
                  <Bar dataKey="ventas" fill="#06B6D4" radius={[6, 6, 0, 0]} name="Ventas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="top-products-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <TrendingUp className="w-5 h-5 text-green-500" />
              Productos Más Vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={topProducts.slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis dataKey="product_name" type="category" width={130} tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <Tooltip formatter={v => [v, 'Unidades']} />
                  <Bar dataKey="quantity" fill="#10B981" radius={[0, 6, 6, 0]} name="Cantidad" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">No hay datos</div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="bottom-products-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <TrendingDown className="w-5 h-5 text-rose-500" />
              Productos Menos Vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bottomProducts.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={bottomProducts.slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis dataKey="product_name" type="category" width={130} tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <Tooltip formatter={v => [v, 'Unidades']} />
                  <Bar dataKey="quantity" fill="#F43F5E" radius={[0, 6, 6, 0]} name="Cantidad" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">No hay datos</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Last invoices table */}
      {salesReport?.last_invoices?.length > 0 && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <FileText className="w-5 h-5 text-cyan-500" />
              Últimas Facturas del Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600">Factura</th>
                    <th className="text-left p-3 font-medium text-slate-600">Cliente</th>
                    <th className="text-left p-3 font-medium text-slate-600">Método</th>
                    <th className="text-left p-3 font-medium text-slate-600">Fecha</th>
                    <th className="text-right p-3 font-medium text-slate-600">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {salesReport.last_invoices.map((inv, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-medium">{inv.invoice_number}</td>
                      <td className="p-3 text-slate-600">{inv.customer_name || 'Consumidor Final'}</td>
                      <td className="p-3 text-slate-600 capitalize">{paymentLabels[inv.payment_method] || inv.payment_method}</td>
                      <td className="p-3 text-slate-500">{inv.created_at?.slice(0, 10)}</td>
                      <td className="p-3 text-right font-bold text-cyan-600">{money(inv.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default Reports;